import React from "react";
import PropTypes from "prop-types";
import { InlineIcon } from "@iconify/react";
import _ from "lodash";
import { createUseStyles } from "react-jss";
import {
  ButtonHOC,
  DefaultBtn,
  DefaultMSFNavBtn,
  DefaultSearchBtn,
} from "../HOCs";
import jssVariables from "../../../../jss_setup";
import {
  commonStylesBasic,
  commonStylesIconBasic,
  commonStylesPlaceholderBasic,
  getCommonStylesLabelBasic,
} from "../helpers";

const commonStyles = {
  ...commonStylesBasic,
  minWidth: "max-content",
  borderRadius: "10px",
  padding: `calc(${jssVariables.normal_gap} / 2) calc(${jssVariables.normal_gap} * 1)`,
  background: jssVariables.colors.red,
  border: `1px solid ${jssVariables.colors.red}`,
  boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
  "&:hover:enabled": {
    color: jssVariables.colors.red,
    background: "white",
  },
  "&:disabled": {
    color: jssVariables.colors.red,
    background: "white",
  },
};

const icon = {
  ...commonStylesIconBasic,
  marginBottom: 0,
};

const ButtonStyles = createUseStyles({
  placeholder: (props) => ({
    ...commonStylesPlaceholderBasic,
    borderRadius: commonStyles.borderRadius,
    ..._.get(props.customStyles, "placeholder", {}),
  }),
  ButtonDefault: (props) => ({
    ...commonStyles,
    display: "grid",
    gridTemplateColumns:
      props.showIcon && props.hasText ? "1fr max-content" : "1fr",
    gridGap: props.showIcon && props.hasText ? jssVariables.half_gap : 0,
    background: !props.empty ? jssVariables.colors.red : "white",
    color: props.empty ? jssVariables.colors.red : "white",
    border: props.empty
      ? `1px solid ${jssVariables.colors.red}`
      : `1px solid ${jssVariables.colors.red}`,
    "&:hover:enabled": {
      color: props.empty ? "white" : jssVariables.colors.red,
      background: props.empty ? jssVariables.colors.red : "white",
    },
    ..._.get(props.customStyles, "ButtonDefault", {}),
  }),
  ButtonReverted: (props) => ({
    ...commonStyles,
    display: "grid",
    gridTemplateColumns:
      props.showIcon && props.hasText ? "1fr max-content" : "1fr",
    gridGap: props.showIcon && props.hasText ? jssVariables.half_gap : 0,
    background: !props.empty ? "white" : "none",
    color: jssVariables.colors.red,
    border: !props.empty
      ? "1px solid white"
      : `1px solid ${jssVariables.colors.red}`,
    "&:hover:enabled": {
      color: "white",
      background: jssVariables.colors.red,
    },

    ..._.get(props.customStyles, "ButtonReverted", {}),
  }),
  label: (props) => ({
    ...getCommonStylesLabelBasic(props),
    textTransform: "uppercase",
    ..._.get(props.customStyles, "label", {}),
  }),
  icon: (props) => ({
    ...icon,
    fontSize: props.size,
    ..._.get(props.customStyles, "icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    ButtonDefault: (props) => ({
      border: props.empty
        ? "1px solid white"
        : `1px solid ${jssVariables.colors.red}`,
      ..._.get(props.customStyles, "ButtonDefault.smallscreen", {}),
    }),
    ButtonReverted: (props) => ({
      background: !props.empty ? jssVariables.colors.red : "none",
      color: "white",
      border: props.empty
        ? "1px solid white"
        : `1px solid ${jssVariables.colors.red}`,
      ..._.get(props.customStyles, "ButtonReverted.smallscreen", {}),
    }),
    label: (props) => ({
      ..._.get(props.customStyles, "label.smallscreen", {}),
    }),
  },
});
var Button = ({ text, showIcon, ...props }) => {
  return (
    <DefaultBtn {...props}>
      <span className={props.classes.label}>{text}</span>
      {showIcon && (
        <InlineIcon
          icon={props.icon ? props.icon : "fa:angle-right"}
          className={props.classes.icon}
        />
      )}
    </DefaultBtn>
  );
};
Button.defaultProps = {
  text: "",
  showIcon: true,
  classes: {},
};
Button.propTypes = {
  text: PropTypes.oneOfType([PropTypes.object, PropTypes.string]).isRequired,
  showIcon: PropTypes.bool,
};
Button = ButtonHOC(Button, ButtonStyles);

//---------------------------------------
//-------- SearchButton -----------------
const SearchButtonStyles = createUseStyles({
  placeholder: (props) => ({
    height: "3rem !important",
    minWidth: "10rem",
    borderRadius: jssVariables.half_gap,
    ..._.get(props.customStyles, "placeholder", {}),
  }),
  SearchButton: (props) => ({
    ...commonStyles,
    minWidth: "6rem",
    background: jssVariables.colors.red,
    border: `2px solid ${jssVariables.colors.red}`,
    color: "white",
    height: "3rem",
    fontSize: "medium",
    "&:hover": {
      background: "white",
      color: jssVariables.colors.red,
    },
    ..._.get(props.customStyles, "SearchButton", {}),
  }),
  label: (props) => ({
    ...getCommonStylesLabelBasic(props),
    fontWeight: "bold",
    textTransform: "uppercase",
    ..._.get(props.customStyles, "label", {}),
  }),
  icon: (props) => ({
    ...icon,
    fontSize: "x-large",
    marginTop: ".2rem",
    ..._.get(props.customStyles, "icon", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {},
});
var SearchButton = ({ loading, text, ...props }) => {
  return (
    <DefaultSearchBtn {...props}>
      {loading ? (
        <InlineIcon
          icon="line-md:loading-twotone-loop"
          className={props.classes.icon}
        />
      ) : (
        <span className={props.classes.label}>{text}</span>
      )}
    </DefaultSearchBtn>
  );
};
SearchButton.defaultProps = {
  loading: false,
  text: "",
};
SearchButton.propTypes = {
  loading: PropTypes.bool,
  text: PropTypes.string.isRequired,
};
SearchButton = ButtonHOC(SearchButton, SearchButtonStyles);

const MSFNavBtnStyles = createUseStyles({
  placeholder: (props) => ({
    height: "2.5rem!important",
    minWidth: "4rem!important",
    borderRadius: commonStyles.borderRadius,
    ..._.get(props.customStyles, "placeholder", {}),
  }),
  button: (props) => ({
    ...jssVariables.fonts,
    display: props.showIcon ? "grid" : "block",
    gridTemplateColumns: "1fr max-content",
    background: jssVariables.colors.background.light,
    borderRadius: "2rem",
    border: `2px solid transparent`,
    borderBottom: `2px solid transparent`,
    fontWeight: "normal",
    padding: [0, jssVariables.normal_gap],
    height: "3rem",
    alignItems: "center",
    color: jssVariables.colors.red,
    "&:hover:enabled": {
      background: jssVariables.colors.red,
      color: jssVariables.colors.text.light,
    },
    "&:disabled": {
      background: jssVariables.colors.red,
      color: jssVariables.colors.text.light,
      cursor: "unset",
    },
    ..._.get(props.customStyles, "ButtonDefault", {}),
  }),
  label: (props) => ({
    ...getCommonStylesLabelBasic(props),
    textTransform: "uppercase",
    fontWeight: "bold",
    ..._.get(props.customStyles, "label", {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    button: (props) => ({
      ..._.get(props.customStyles, "button.smallscreen", {}),
    }),
  },
});
var MSFNavBtn = ({ text, ...props }) => {
  return (
    <DefaultMSFNavBtn {...props}>
      <span className={props.classes.label}>{text}</span>
    </DefaultMSFNavBtn>
  );
};
MSFNavBtn.defaultProps = {
  text: "",
  classes: {},
  label: "",
};
MSFNavBtn.propTypes = {
  text: PropTypes.string.isRequired,
  classes: PropTypes.object,
  label: PropTypes.string,
};
MSFNavBtn = ButtonHOC(MSFNavBtn, MSFNavBtnStyles);

export { Button, SearchButton, MSFNavBtn };
